import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';

import { noWheelInputProps } from 'util/utils';
import { validateMenuItemName } from 'util/validation';
import ColorField from './ColorField';

const emptyForm = () => ({ name: '', shortName: '', price: '', hideForWeb: false, default: false, isPrintDisabled: false, colorHex: null });

const formFrom = (option) => ({
  ...emptyForm(),
  name: option.name || '',
  shortName: option.shortName || '',
  price: option.price ?? '',
  hideForWeb: !!option.hideForWeb,
  default: !!(option.default ?? option.isDefault),
  colorHex: option.colorHex || null,
});

/**
 * Add a choice, add a choice linked to a dish, or edit a choice.
 * `onSave(form, link)` resolves to true when saved.
 */
const OptionDialog = ({ open, mode, option, categories, currencySymbol, onClose, onSave }) => {
  const [form, setForm] = useState(emptyForm());
  const [linkCategory, setLinkCategory] = useState(null);
  const [linkDish, setLinkDish] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(mode === 'edit' && option ? formFrom(option) : emptyForm());
    setLinkCategory(null);
    setLinkDish(null);
  }, [open, mode, option]);

  const set = (key) => (event) => setForm({ ...form, [key]: event.target.value });
  const toggle = (key) => (event) => setForm({ ...form, [key]: event.target.checked });

  const nameError = validateMenuItemName(form.name);
  const priceMissing = form.price === '' || form.price === null || Number.isNaN(Number(form.price));
  const blocked = !form.name || !!nameError || priceMissing || (mode === 'link' && !linkDish);

  const pickDish = (dish) => {
    setLinkDish(dish);
    if (dish) {
      setForm({ ...form, name: dish.name || '', price: dish.price ?? '', shortName: dish.shortName || '' });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const link =
        mode === 'link' ? { menuItemId: linkDish.id, menuItemName: linkDish.name, category: linkCategory.name } : null;
      if (await onSave(form, link)) {
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const title = mode === 'edit' ? 'Edit choice' : mode === 'link' ? 'Add a dish as a choice' : 'Add choice';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {mode === 'link' && (
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Autocomplete
                options={categories}
                getOptionLabel={(category) => category.name || 'Untitled'}
                value={linkCategory}
                onChange={(event, value) => {
                  setLinkCategory(value);
                  pickDish(null);
                }}
                renderInput={(params) => <TextField {...params} label="Category" />}
                sx={{ flex: 1 }}
              />
              <Autocomplete
                options={(linkCategory && linkCategory.items) || []}
                getOptionLabel={(dish) => dish.name || 'Untitled'}
                value={linkDish}
                onChange={(event, value) => pickDish(value)}
                disabled={!linkCategory}
                renderInput={(params) => <TextField {...params} label="Dish" />}
                sx={{ flex: 1 }}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField label="Name" required fullWidth value={form.name} onChange={set('name')} error={!!nameError} helperText={nameError || ' '} />
            {mode === 'edit' && (
              <Tooltip title="Make the name and short name lower case">
                <Button
                  sx={{ mt: 1, minWidth: 48 }}
                  onClick={() => setForm({ ...form, name: form.name.toLowerCase(), shortName: form.shortName.toLowerCase() })}
                >
                  Aa
                </Button>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Short name" value={form.shortName} onChange={set('shortName')} sx={{ flex: 1 }} />
            <TextField
              label="Price"
              required
              type="number"
              value={form.price}
              onChange={set('price')}
              InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }}
              inputProps={noWheelInputProps}
              sx={{ flex: 1 }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel control={<Switch checked={form.default} onChange={toggle('default')} />} label="Selected by default" />
            <FormControlLabel control={<Switch checked={form.hideForWeb} onChange={toggle('hideForWeb')} />} label="Hide from web ordering" />
            {mode === 'link' && (
              <FormControlLabel control={<Switch checked={form.isPrintDisabled} onChange={toggle('isPrintDisabled')} />} label="Don't print" />
            )}
          </Box>
          {mode === 'edit' && <ColorField label="Colour" value={form.colorHex} onChange={(colorHex) => setForm({ ...form, colorHex })} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={save} disabled={blocked || saving}>
          {mode === 'edit' ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

OptionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['new', 'link', 'edit']).isRequired,
  option: PropTypes.object,
  categories: PropTypes.array.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default OptionDialog;
